/* eslint-disable react/display-name, @typescript-eslint/ban-types */
import { ComponentType, ErrorInfo } from 'react'
import { withErrorBoundary } from 'react-error-boundary'
import { reportErrorBoundaryToSentry } from '@/lib/sentry-report-boundary'
import { Error404 } from '@/pages/Error404'
import { Error401 } from '@/pages/Error401'
import { Error500 } from '@/pages/Error500'
import ErrorUnknown from '@/pages/ErrorUnknown'

interface FallbackProps {
  error: Error
  resetErrorBoundary: () => void
}

export const CustomErrorFallbacks: React.FC<FallbackProps> = ({
  error,
  resetErrorBoundary,
}: {
  error: Error
  resetErrorBoundary: Function
}) => {
  if (error.message.includes('401')) {
    return <Error401 />
  }
  if (error.message.includes('404')) {
    return <Error404 />
  }
  if (error.message.includes('500')) {
    return <Error500 />
  }

  return <ErrorUnknown />
}

type WithErrorBoundaryOptions = {
  onError?: (error: Error, info: ErrorInfo) => void
  onReset?: () => void
}

export function wrapWithErrorBoundary(
  Component: ComponentType<any>,
  options: WithErrorBoundaryOptions = {}
): ComponentType<any> {
  const { onError: userOnError, ...restOptions } = options
  // Le composant enveloppé est créé une seule fois, à l'appel. Le créer dans une
  // fonction de rendu donnerait à React un nouveau type à chaque rendu du parent :
  // la page serait démontée puis remontée (état local perdu, popovers fermés) à
  // chaque changement d'URL (TEC-1640).
  return withErrorBoundary(Component, {
    FallbackComponent: CustomErrorFallbacks,
    ...restOptions,
    onError: (error: Error, info: ErrorInfo) => {
      reportErrorBoundaryToSentry(error, info)
      userOnError?.(error, info)
    },
  })
}
